import { Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Link2, Plus, Rss, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '../../components/page-header'
import { createCalendarFeed, deleteCalendarFeed, listCalendarFeeds } from '../../lib/calendar-api'

/** Absolute subscription URL for an iCal app (relative path -> origin + /api). */
function feedUrl(relPath: string): string {
  return `${window.location.origin}/api${relPath}`
}

export function CalendarFeedsPage() {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  const feedsQuery = useQuery({
    queryKey: ['calendar-feeds'],
    queryFn: listCalendarFeeds,
  })

  const create = useMutation({
    mutationFn: () => createCalendarFeed({ name: name.trim() }),
    onSuccess: () => {
      setName('')
      qc.invalidateQueries({ queryKey: ['calendar-feeds'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteCalendarFeed(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-feeds'] }),
  })

  async function copy(relPath: string, id: string) {
    try {
      await navigator.clipboard.writeText(feedUrl(relPath))
      setCopied(id)
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1500)
    } catch {
      /* clipboard unavailable */
    }
  }

  const feeds = feedsQuery.data ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        title="Calendar feeds"
        description="Publish a tokenized iCal subscription URL. Public feeds expose only public events."
      />

      <Card className="space-y-4 p-5">
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) create.mutate()
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="feed-name">New feed name</Label>
            <Input
              id="feed-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Public events"
              maxLength={160}
            />
          </div>
          <Button type="submit" disabled={!name.trim() || create.isPending}>
            <Plus />
            {create.isPending ? 'Creating…' : 'Create feed'}
          </Button>
        </form>
        {create.isError && (
          <p className="text-sm text-danger">
            {create.error instanceof Error ? create.error.message : 'Could not create feed.'}
          </p>
        )}
      </Card>

      <Card className="overflow-hidden p-0">
        {feedsQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted">
            <Spinner /> Loading feeds…
          </div>
        ) : feedsQuery.isError ? (
          <div className="space-y-2 py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load feeds.</p>
            <p className="text-xs text-dim">
              {feedsQuery.error instanceof Error
                ? feedsQuery.error.message
                : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : feeds.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <Rss className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              No feeds yet. Create one to give people a subscribe-able calendar URL.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {feeds.map((feed) => (
              <li key={feed.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="grid size-9 shrink-0 place-items-center rounded-md bg-card-hover text-secondary">
                  <Link2 className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{feed.name}</div>
                  <div className="truncate font-mono text-xs text-dim">{feedUrl(feed.url)}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copy(feed.url, feed.id)}
                  aria-label={`Copy subscribe URL for ${feed.name}`}
                >
                  {copied === feed.id ? <Check /> : <Copy />}
                  {copied === feed.id ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Delete feed ${feed.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(feed.id)}
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
